import { useState, useEffect, useRef } from "react";

// Libraries
import DOMPurify from "dompurify";

// Components
import { OverlayPanel } from "primereact/overlaypanel";
// eslint-disable-next-line import/no-unresolved
import { MenuItem } from "primereact/menuitem";
import { PanelMenu } from "primereact/panelmenu";
import { Avatar } from "primereact/avatar";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

// API
import {
  fetchUserNotifications,
  clearUserNotification,
  clearAllUserNotifications,
} from "../../../../api";
import { Notification } from "../../../../api/types";
import { initNotificationsChannel } from "../../../../client/actionCable";

// Styles & Icons
import S from "./notifications.module.scss";

export const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const op = useRef<OverlayPanel>(null);
  const bellButtonRef = useRef<Button>(null);

  const loadNotifications = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await fetchUserNotifications();
      setNotifications(data.notifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
    setLoading(false);
  };

  // Get notifications and setup ActionCable
  useEffect(() => {
    loadNotifications();
    const channel = initNotificationsChannel((message: { status: boolean }) => {
      if (message.status) {
        loadNotifications();
      }
    });

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const handleNoticeDelete = async (id: string | number): Promise<void> => {
    setNotifications(notifications.filter(n => n.id !== id));
    await clearUserNotification(Number(id));
  };

  const handleDeleteAll = async (): Promise<void> => {
    setLoading(true);
    setNotifications([]);
    await clearAllUserNotifications();
    setLoading(false);
  };

  // Whenever notifications change, realign the overlay panel
  useEffect(() => {
    op.current?.align();
  }, [notifications]);

  // Avatar Dropdown Item Template
  const notificationsItemTemplate = (item: Notification) => {
    const { id, content, createdAt } = item;
    return (
      <div key={id} className={S.panelMenuItem}>
        <div>
          <div
            className="body-sm"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
          ></div>{" "}
          <small>{createdAt}</small>
        </div>
        <Button
          size="small"
          rounded
          text
          className={S.deleteButton}
          icon="pi pi-times"
          aria-label="Dismiss notification"
          onClick={() => handleNoticeDelete(id)}
        />
      </div>
    );
  };

  const loaderTemplate = () => {
    return (
      <div className={S.panelMenuItem}>
        <ProgressSpinner aria-label="Loading" style={{ width: "1rem", height: "1rem" }} />
      </div>
    );
  };

  const getMenuModel = (): MenuItem[] => {
    if (loading) {
      return [{ template: loaderTemplate }];
    }

    if (notifications.length) {
      return notifications.map(n => ({ template: () => notificationsItemTemplate(n) }));
    }

    return [{ template: () => <p className={S.panelMenuItem}>No notifications</p> }];
  };

  const handleToggle = (e: React.SyntheticEvent) => {
    op.current?.toggle(e);
    setIsPanelOpen(prev => !prev);
  };

  const handleClose = () => {
    op.current?.hide();
    setIsPanelOpen(false);
    // Return focus to the bell button
    (bellButtonRef.current as any)?.getElement?.()?.focus();
  };

  // Close overlay on Escape and return focus to bell button
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPanelOpen]);

  return (
    <>
      <Button
        ref={bellButtonRef}
        type="button"
        unstyled
        className="cursor-pointer p-overlay-badge z-10 inline-flex items-center bg-transparent"
        onClick={handleToggle}
        aria-label={`Notifications${notifications.length ? `, ${notifications.length} unread` : ""}`}
        aria-haspopup="true"
        aria-expanded={isPanelOpen}
      >
        <Avatar icon="pi pi-bell" size="normal" shape="circle" className="avatar">
          {notifications.length ? <Badge value={notifications.length}></Badge> : null}
        </Avatar>
      </Button>
      <OverlayPanel
        ref={op}
        dismissable={true}
        onHide={() => setIsPanelOpen(false)}
        pt={{ content: { className: "pr-0" } }}
      >
        <PanelMenu className={S.notificationsPanel} model={getMenuModel()} />
        {notifications.length ? (
          <div className="flex justify-center border-t border-surface-border pt-4">
            <Button label="Clear All" size="small" onClick={handleDeleteAll} />
          </div>
        ) : null}
      </OverlayPanel>
    </>
  );
};
